// 剣舞の新しい技「回って終わる連」(2026-10-25・開発版 ?bladeSpin=1)。アルレッキーノ (Lies of P) の、爪先立ちの構えから低く回って何度も薙ぎ、最後に大きく薙ぐ連を落とし込んだ。
//   作者「剣舞の技の数は全然増やしていいよ。ひとまずアルレッキーノを剣舞に落とし込む形で作ってもらって、実プレイしながら調整していきたい。」
//   技の組み立て = docs/research/2026-10-25-arlecchino-attack-analysis.md (回って終わる連 D)。爪・稲妻は外し、ワルツの回転と剣だけで見せる。
//   流れ: 爪先で立ち、片腕を頭上・片腕を水平に構える (見得) → ① 体を寝かせて低く回りながら前を薙ぐ → 爪先で立ち直す
//         → ② 背を向けて腕を広げ、回りながら斬る → 深く沈む (ずらしの一拍) → ③ 両腕を広げて全周を薙ぎきる → 立ち姿。
//   10-28 3発を別々の絵にした (②は前は ① と同じ r2 だった)。五連のように1発ずつ違う形を見せる。
//   3発とも弾ける。回っている間は滑って寄る (旋回突き b4 と同じ寄り方)。締めの③だけ後ろまで届く (全周)。
//   10-28 夜 配布版へ入れた。回る専用の絵 (2版目) で一周する間にも当たりを2回ずつ置き、① → 回る2発 → ② → 回る2発 → ③ の7発にした (下の spinTurnHitsTimeline)。
//   絵 = public/sprites/bosses/blade-dancer/m1025_r_{1..4}.png (横の基準は sprites.ts の剣舞の表)。画面 px は絵の画素 × 0.2718。
//   剣先 (画面 px・足元から前, 上): 構え r1 (151, 116)・薙ぎ r2 (162, 66) と後ろ (−126, 59)・背 r3 (152, 119)・全周 r4 (165, 54) と後ろ (−144, 54)。
import { ATTACKS } from './attacks'
import { BOSS_DEFINITIONS } from './bosses'
import { BOSS_ATTACK_TIMELINES, type BossAttackTimeline } from './boss_pose_timeline'
import { SMEAR_ARTS, preloadBladePetals, type ContactSmearArc } from './render'
import type { AttackDef } from './types'
import { BLADE_SMEAR_BASE, bladeDancerV6Smear, TENSE, withDissolves, type BladeTrailStyle } from './blade_dancer_art'
import { tidyRecovery } from './blade_dancer_waltz'

const BOSS_ID = 'blade-dancer'
export const BLADE_SPIN_ID = 'bladeSpinChain'
const R = (n: number) => `m1025_r_${n}`
const SETTLE = 'm1020_waltz_s2'
const STAND = 'm1020_waltz_c6'

// 拍 (ms)。①→② は 0.6 秒、②→③ は沈む一拍を足して 0.85 秒 (手本の締めの遅れ)。当たりから次へ移る所の1コマ分を先に引く。
const TICK = 16
const ACTIVE_MS = 130
const BEAT_MS = 600
const SINK_MS = 250
export const BLADE_SPIN_GAPS_MS = [BEAT_MS - ACTIVE_MS - TICK, BEAT_MS + SINK_MS - ACTIVE_MS - TICK]
const CHARGE_MS = 900
const RECOVERY_MS = 1050

export const BLADE_SPIN_FRONT_PX = [162, 152, 165] as const
const REACH_FIT_MARGIN_PX = 15
const REAR_PX = -100
const FULL_REAR_PX = -144              // ③ 全周: 後ろの剣先まで当たる
export const BLADE_SPIN_STRIKES = [
  { localFromPx: REAR_PX, localToPx: BLADE_SPIN_FRONT_PX[0] + REACH_FIT_MARGIN_PX },
  { localFromPx: REAR_PX, localToPx: BLADE_SPIN_FRONT_PX[1] + REACH_FIT_MARGIN_PX },
  { localFromPx: FULL_REAR_PX, localToPx: BLADE_SPIN_FRONT_PX[2] + REACH_FIT_MARGIN_PX },
] as const
const STEP_IN_MS = 200
const STOP_BUFFER_PX = 8
const LUNGE_PX = 220
const APPROACH_PX = Math.min(...BLADE_SPIN_STRIKES.map(s => s.localToPx))

export const BLADE_SPIN_TIMELINE: BossAttackTimeline = withDissolves([
  { phase: 'telegraph', at: 0, image: SETTLE, hold: true, tag: 'anticipation', soft: true },
  { phase: 'telegraph', at: 0.12, image: R(1), hold: true, ease: 'easeIn' },                // 爪先で立ち、片腕を頭上・片腕を水平に構える (見得)
  { phase: 'telegraph', at: 0.8, image: R(1), hold: true, ...TENSE },
  // 10-28 1発ずつ違う絵にした (配布版の五連・三連の作り)。①低い薙ぎ r2 → ②背を向けて腕を広げた回し斬り r3 → ③沈んで全周 r4。
  //   振り抜いた絵は前へ流し (DRIVE)、次の構えで止めて溜め (TENSE)、一気に当たる絵へ。
  { phase: 'active', at: 0, image: R(2), hold: true, tag: 'contact', hitIndex: 0 },         // ① 体を寝かせて低く回りながら前を薙ぐ
  // 10-28 作者「花嫁のエフェクトを付けてほしいわけではない」「前の方が良い」。間は回る途中の借り物の絵を挟まない前の版に戻した。
  //   回って見せるのは、回る専用に描いた絵を角度で切り替える形で別に作る (花嫁の BRIDE_TURN_ART と同じやり方)。
  { phase: 'comboGap', at: 0, image: R(2), hold: true, tag: 'followThrough', hitIndex: 0, offset: { x: 10 }, rot: 3, ease: 'easeOut' },
  { phase: 'comboGap', at: 0.45, image: R(1), hold: true, hitIndex: 0, ease: 'easeOut' },   // 爪先で立ち直して剣を前へ揃える
  { phase: 'comboGap', at: 0.78, image: R(1), hold: true, hitIndex: 0, ...TENSE, ease: 'easeIn' },
  { phase: 'active', at: 0, image: R(3), hold: true, tag: 'contact', hitIndex: 1 },         // ② 背を向けて回りながら、腕を広げて胸の高さを薙ぐ
  { phase: 'comboGap', at: 0, image: R(3), hold: true, tag: 'followThrough', hitIndex: 1, rot: -4, ease: 'easeOut' },
  { phase: 'comboGap', at: 0.3, image: R(2), hold: true, hitIndex: 1, ease: 'easeOut' },     // 前へ回り戻って低く
  { phase: 'comboGap', at: 0.62, image: R(2), hold: true, hitIndex: 1, scale: { x: 1.04, y: 0.94 }, ...TENSE, ease: 'easeIn' },   // 沈む (ずらしの一拍)
  { phase: 'active', at: 0, image: R(4), hold: true, tag: 'contact', hitIndex: 2 },         // ③ 両腕を広げて全周を薙ぎきる
  { phase: 'recovery', at: 0, image: R(4), hold: true, tag: 'followThrough', offset: { x: 8 }, rot: 2, ease: 'easeOut' },
  // 10-28 締めは沈んだ r4 から立ち姿へ一足に飛ばず、五連・三連と同じ s1 → s2 → 立ち姿の段で起き上がる。
  { phase: 'recovery', at: 0.3, image: 'm1020_v6_g2', hold: true, tag: 'recover', ease: 'easeOut', soft: true },   // 10-28 低い踏み込みから半ば立ち上がる絵を挟む (剣を掲げる s1 へ一足に跳ねない)
  { phase: 'recovery', at: 0.5, image: SETTLE, hold: true, ease: 'easeOut', soft: true },
  { phase: 'recovery', at: 0.7, image: STAND, hold: true, ease: 'easeOut' },
  { phase: 'recovery', at: 0.9, image: 'idle', hold: true },
], { telegraph: CHARGE_MS, recovery: RECOVERY_MS })

// 10-28 回る専用の絵で一周を見せる比較版 (開発版 ?bladeSpinTurn=1)。花嫁の BRIDE_TURN_ART と同じく、同じ姿勢を
//   横・斜め後ろ・後ろ・正面から描いた4枚 (m1028_s_*) と、その左右反転 (*_m) を角度の順に並べて切り替える。効果は足さない。
//   順 = 構え(左向きの斜め前) → 横 → 斜め後ろ → 後ろ → 斜め後ろ反転 → 横反転 → 正面 → 次の構え。
// 10-28 夕 2版目 (?bladeSpinTurn=2・m1028_s2_*)。1版目は剣が振りの絵の約6割で細く、T 字の棒立ちで冠が垂れていたので、
//   剣の長さ・太さを立ち絵と同じにし、傾いて回る姿勢で冠とスカートがなびく形に描き直した。1版目は比べるために残す。
const TURN_PREFIX: Record<number, string> = { 1: 'm1028_s_', 2: 'm1028_s2_' }
// 剣舞の絵は左向き (主人公の方) が元。振りの絵と同じ向きの横から、斜め後ろ→後ろ→反対の斜め後ろ→反対の横→正面と一周させる。
const turnFull = (prefix: string) => ['side', 'back3q', 'back', 'back3q_m', 'side_m', 'front'].map(v => prefix + v)
/** comboGap の [from, to) に回る絵を等分に並べる。 */
function turnKeys(hitIndex: number, names: readonly string[], from: number, to: number): BossAttackTimeline {
  const step = (to - from) / names.length
  return names.map((image, i) => ({ phase: 'comboGap' as const, at: from + step * i, image, hold: true, hitIndex }))
}
function spinTurnTimeline(prefix: string): BossAttackTimeline {
  const TURN_FULL = turnFull(prefix)
  return withDissolves([
    ...BLADE_SPIN_TIMELINE.filter(k => k.phase === 'telegraph' || (k.phase === 'active' && k.hitIndex === 0)),
    { phase: 'comboGap', at: 0, image: R(2), hold: true, tag: 'followThrough', hitIndex: 0, ease: 'easeOut' },
    ...turnKeys(0, TURN_FULL, 0.2, 0.74),
    { phase: 'comboGap', at: 0.74, image: R(1), hold: true, hitIndex: 0, ...TENSE, ease: 'easeIn' },
    { phase: 'active', at: 0, image: R(3), hold: true, tag: 'contact', hitIndex: 1 },
    { phase: 'comboGap', at: 0, image: R(3), hold: true, tag: 'followThrough', hitIndex: 1, ease: 'easeOut' },
    ...turnKeys(1, TURN_FULL, 0.12, 0.5),
    { phase: 'comboGap', at: 0.5, image: R(2), hold: true, hitIndex: 1, ease: 'easeOut' },
    { phase: 'comboGap', at: 0.62, image: R(2), hold: true, hitIndex: 1, scale: { x: 1.04, y: 0.94 }, ...TENSE, ease: 'easeIn' },
    ...BLADE_SPIN_TIMELINE.filter(k => (k.phase === 'active' && k.hitIndex === 2) || k.phase === 'recovery'),
  ], { telegraph: CHARGE_MS, recovery: RECOVERY_MS })
}
export const BLADE_SPIN_TURN_TIMELINE: BossAttackTimeline = spinTurnTimeline(TURN_PREFIX[1])
export const BLADE_SPIN_TURN2_TIMELINE: BossAttackTimeline = spinTurnTimeline(TURN_PREFIX[2])

// 10-28 夜 作者 (M-3)「回るタイミングで攻撃判定が２回欲しい」「１セットの回転につき２ヒット分」。
//   回る絵の一周を前半 (横→斜め後ろ→後ろ) と後半 (反対の斜め後ろ→反対の横→正面) に分け、半周ごとに当たりを1回置く。
//   回るのは ① の後と ② の後の2回なので、当たりは ① → 回る2発 → ② → 回る2発 → ③ の7発。
//   回る2発は速い連と同じ 0.3 秒ごとで、1発ずつ弾ける。回る分だけ連は長くなる (①→② 0.6→1.1 秒・②→③ 0.85→1.2 秒)。
//   ダメージと体幹は前の3発の合計 (42・68) を7発に割る。前の3発の形は ?bladeSpinHits=old。
const TURN_IN_MS = 350              // 振った当たりから回る1発目まで (横を向いて回り始める)
const TURN_STEP_MS = 300            // 回る2発の間 (速い連と同じ)
const TURN_OUT_MS = 450             // 回る2発目から ② まで (正面へ回りきり、構えて溜める)
const TURN_SINK_MS = TURN_OUT_MS + 100   // 回る2発目から ③ まで (沈む一拍)
const gapOf = (beat: number) => beat - ACTIVE_MS - TICK
export const BLADE_SPIN_TURN_HITS_GAPS_MS = [TURN_IN_MS, TURN_STEP_MS, TURN_OUT_MS, TURN_IN_MS, TURN_STEP_MS, TURN_SINK_MS].map(gapOf)
// 10-28 深夜 作者 (M-5)「周り２連の攻撃判定のタイミングをもう少し間を詰めてほしい。今をタンタンだとしたらタタにしてほしい。２回の回転両方とも」。
//   1周の中の2発の間を半分 (0.3 → 0.15 秒) にする。2発目の頭は1発目の当たりが終わってすぐ (当たり 0.13 秒 + 1コマ)。
//   弾く側は1発目を弾いた直後に押し直せば2発目も弾ける (弾けた後の押し直しは即座に受け付ける・player.ts)。
//   半周 (斜め後ろ → 後ろ → 反対の斜め後ろ) は1発目の当たりの間に回りきる。前の 0.3 秒の形は開発版 ?bladeSpinPair=old。
const TURN_PAIR_MS = 150
export const BLADE_SPIN_TURN_PAIR_GAPS_MS = [TURN_IN_MS, TURN_PAIR_MS, TURN_OUT_MS, TURN_IN_MS, TURN_PAIR_MS, TURN_SINK_MS].map(gapOf)
// 回る当たりの届きは ① の低い薙ぎと同じ。回る絵の剣先は前へ 45〜75px しか出ないので (体どうしの最短 108px より近い)、
//   当たる瞬間に剣先の跡 (配布版の残像の帯) を ① と同じ弧で出して届きを見せる。花嫁の風と帯は使わない。
export const BLADE_SPIN_TURN_STRIKE = { localFromPx: REAR_PX, localToPx: BLADE_SPIN_FRONT_PX[0] + REACH_FIT_MARGIN_PX } as const
/** 7発のうち回る当たりの段の番号。 */
export const BLADE_SPIN_TURN_HIT_INDEXES = [1, 2, 4, 5] as const
const TURN_HIT_ART = ['back3q', 'side_m'] as const   // 前半・後半の半周で当たる絵

function spinTurnHitsTimeline(prefix: string, pair = false): BossAttackTimeline {
  const t = (v: string) => prefix + v
  /** 振った当たり h の後に一周して、h+1 (前半) と h+2 (後半) で当たる。pair = 2発の間を詰めた形 (半周を1発目の当たりの間に回る)。 */
  const turn = (h: number, from: string): BossAttackTimeline => [
    { phase: 'comboGap', at: 0, image: from, hold: true, tag: 'followThrough', hitIndex: h, ease: 'easeOut' },
    { phase: 'comboGap', at: 0.5, image: t('side'), hold: true, hitIndex: h },
    { phase: 'active', at: 0, image: t(TURN_HIT_ART[0]), hold: true, tag: 'contact', hitIndex: h + 1 },
    ...(pair ? [
      { phase: 'active' as const, at: 0.38, image: t('back'), hold: true, hitIndex: h + 1 },
      { phase: 'active' as const, at: 0.77, image: t('back3q_m'), hold: true, hitIndex: h + 1 },
      { phase: 'comboGap' as const, at: 0, image: t('back3q_m'), hold: true, hitIndex: h + 1 },
    ] : [
      { phase: 'comboGap' as const, at: 0, image: t('back'), hold: true, hitIndex: h + 1 },
      { phase: 'comboGap' as const, at: 0.5, image: t('back3q_m'), hold: true, hitIndex: h + 1 },
    ]),
    { phase: 'active', at: 0, image: t(TURN_HIT_ART[1]), hold: true, tag: 'contact', hitIndex: h + 2 },
    { phase: 'comboGap', at: 0, image: t('front'), hold: true, tag: 'followThrough', hitIndex: h + 2, ease: 'easeOut' },
  ]
  return withDissolves([
    ...BLADE_SPIN_TIMELINE.filter(k => k.phase === 'telegraph' || (k.phase === 'active' && k.hitIndex === 0)),
    ...turn(0, R(2)),
    { phase: 'comboGap', at: 0.35, image: R(1), hold: true, hitIndex: 2, ease: 'easeOut' },   // 正面へ回りきって構え直す
    { phase: 'comboGap', at: 0.7, image: R(1), hold: true, hitIndex: 2, ...TENSE, ease: 'easeIn' },
    { phase: 'active', at: 0, image: R(3), hold: true, tag: 'contact', hitIndex: 3 },
    ...turn(3, R(3)),
    { phase: 'comboGap', at: 0.3, image: R(2), hold: true, hitIndex: 5, ease: 'easeOut' },     // 前へ回り戻って低く
    { phase: 'comboGap', at: 0.6, image: R(2), hold: true, hitIndex: 5, scale: { x: 1.04, y: 0.94 }, ...TENSE, ease: 'easeIn' },   // 沈む
    ...BLADE_SPIN_TIMELINE.filter(k => k.phase === 'active' && k.hitIndex === 2).map(k => ({ ...k, hitIndex: 6 })),
    ...BLADE_SPIN_TIMELINE.filter(k => k.phase === 'recovery'),
  ], { telegraph: CHARGE_MS, recovery: RECOVERY_MS })
}
/** 配布版の形にする: 出だしの構えは描き直した r_1b、戻りは振り抜き → 両腕を広げる締め → 立ち姿の3枚 (アルレ版の戻りと同じ)。 */
function released(tl: BossAttackTimeline): BossAttackTimeline {
  return tidyRecovery(BLADE_SPIN_ID, tl).map(k => k.image === R(1) ? { ...k, image: 'm1025_r_1b' } : k)
}
export const BLADE_SPIN_TURN_HITS_TIMELINE: BossAttackTimeline = released(spinTurnHitsTimeline(TURN_PREFIX[1]))
export const BLADE_SPIN_TURN2_HITS_TIMELINE: BossAttackTimeline = released(spinTurnHitsTimeline(TURN_PREFIX[2]))
export const BLADE_SPIN_TURN_PAIR_TIMELINE: BossAttackTimeline = released(spinTurnHitsTimeline(TURN_PREFIX[1], true))
export const BLADE_SPIN_TURN2_PAIR_TIMELINE: BossAttackTimeline = released(spinTurnHitsTimeline(TURN_PREFIX[2], true))

// 10-28 夜 作者 (M-4)「入れてよい」で、2版目の回る絵が既定。0 = 回る絵を使わない前の版・1 = 1版目は比べるために残す。
let turnVer = 2
/** 回る専用の絵の版にする (0 = 使わない・1 = 1版目・2 = 2版目)。useBladeDancerSpin / useBladeDancerArle より先に呼ぶ。 */
export function setBladeSpinTurn(v: number | boolean): void { turnVer = v === true ? 1 : v === false ? 0 : v }
export function parseBladeSpinTurn(v: string | null): number { return v === '0' ? 0 : v === '1' ? 1 : 2 }
let oldHits = false
/** 回る間の当たりが無い前の3発の形にする (?bladeSpinHits=old)。useBladeDancerSpin より先に呼ぶ。 */
export function setBladeSpinOldHits(v: boolean): void { oldHits = v }
export function parseBladeSpinHits(v: string | null): boolean { return v === 'old' }
let widePair = false
/** 1周の2発の間を前の 0.3 秒に戻す (?bladeSpinPair=old)。useBladeDancerSpin より先に呼ぶ。 */
export function setBladeSpinWidePair(v: boolean): void { widePair = v }
export function parseBladeSpinPair(v: string | null): boolean { return v === 'old' }

// 剣先の通り道。どちらも中心を床の下に置いた平らな弧で、後ろの剣先から前の剣先まで腰の高さを流れる (剣先 = anchor + 半径 × (cos, sin))。
export const BLADE_SPIN_SMEAR: Record<string, ContactSmearArc> = {
  // ①② 低い薙ぎ: 後ろ (−132, 60) から前の剣先 (162, 66) へ。
  [R(2)]: { ...BLADE_SMEAR_BASE, anchorFwdPx: 20, anchorUpPx: -200, radiusPx: 301.5, angleFromRad: 2.1, angleToRad: 1.0805, widthPx: 8, trailHeadStart: 0.35 },
  // ② 回し斬り: 同じ平らな弧を胸の高さへ上げる。後ろ (−142, 113) から前の剣先 (152, 119) へ。
  [R(3)]: { ...BLADE_SMEAR_BASE, anchorFwdPx: 10, anchorUpPx: -147, radiusPx: 301.5, angleFromRad: 2.1, angleToRad: 1.0805, widthPx: 8, trailHeadStart: 0.35 },
  // ③ 全周: 後ろの剣先 (−144, 54) から前の剣先 (165, 54) へ、床すれすれを長く。
  [R(4)]: { ...BLADE_SMEAR_BASE, anchorFwdPx: 10, anchorUpPx: -400, radiusPx: 479.7, angleFromRad: 1.8955, angleToRad: 1.2423, widthPx: 9, trailHeadStart: 0.35,
    petals: { spread: 'up', count: 14, seed: 37 } },
}
// 回る当たりの跡: ① の低い薙ぎと同じ弧 (後ろから前の剣先 (162, 66) へ・腰の高さ)。回る絵の剣より先まで出て、当たりの届きを見せる。
export const BLADE_SPIN_TURN_SMEAR: Record<string, ContactSmearArc> = Object.fromEntries(
  Object.values(TURN_PREFIX).flatMap(prefix => TURN_HIT_ART.map(v => [prefix + v, { ...BLADE_SPIN_SMEAR[R(2)]! }])),
)
// 詰めた2発 (タタ) の1発目の跡: 0.15 秒後に2発目の跡が出るので、それまでに出しきって消す (配布版の帯は 70ms 保って 200ms で消える)。
//   消える前に2発目へ切り替わると、帯が途中で途切れて見える。
export const BLADE_SPIN_PAIR_FIRST_AFTER_CONTACT = { fromP: 0.45, revealMs: 30, holdMs: 35, fadeMs: 80 } as const

const DEF: AttackDef = {
  id: BLADE_SPIN_ID,
  label: '回って終わる連',
  kind: 'physical',
  chargeMs: CHARGE_MS, activeMs: ACTIVE_MS, recoveryMs: RECOVERY_MS,
  parryable: true, defense: 'parry',
  damage: 14, postureDamage: 20,
  comboHits: 3, comboGapMs: BLADE_SPIN_GAPS_MS[0], comboGapsMs: [...BLADE_SPIN_GAPS_MS],
  range: Math.max(...BLADE_SPIN_STRIKES.map(s => s.localToPx)), approachDistancePx: APPROACH_PX,
  strike: { ...BLADE_SPIN_STRIKES[0] },
  comboStages: [
    { strike: { ...BLADE_SPIN_STRIKES[0] } },
    { strike: { ...BLADE_SPIN_STRIKES[1] } },
    { strike: { ...BLADE_SPIN_STRIKES[2] }, postureDamage: 28 },
  ],
  lungeDistance: LUNGE_PX, gapCloseBuffer: STOP_BUFFER_PX,
  preActiveLungeMs: STEP_IN_MS, preActiveLungeDistance: LUNGE_PX,
  preActiveLungeStopGapPx: APPROACH_PX - STOP_BUFFER_PX, preActiveLungeEase: 'smooth',
  // 回っている間 (連撃の間) だけ滑って寄る。当たる絵の間は止まる。
  comboLunge: { followHits: 3, followSpeed: 380, advanceSpeed: 0, plantFromHit: 3, gapOnly: true, glideRampMs: 120, glideBrakePerSec: 8 },
  sourceBossId: BOSS_ID,
}

// 回る間にも当たる7発 (10-28 夜)。回る当たりは ① と同じ届きで、ダメージと体幹は前の3発の合計を割る。
const turnStage = { strike: { ...BLADE_SPIN_TURN_STRIKE }, damage: 5, postureDamage: 8 }
export const DEF_TURN_HITS: AttackDef = {
  ...DEF,
  damage: 6, postureDamage: 10,
  comboHits: 7, comboGapMs: BLADE_SPIN_TURN_HITS_GAPS_MS[0], comboGapsMs: [...BLADE_SPIN_TURN_HITS_GAPS_MS],
  comboStages: [
    { strike: { ...BLADE_SPIN_STRIKES[0] }, damage: 6, postureDamage: 10 },
    { ...turnStage }, { ...turnStage },
    { strike: { ...BLADE_SPIN_STRIKES[1] }, damage: 6, postureDamage: 10 },
    { ...turnStage }, { ...turnStage },
    { strike: { ...BLADE_SPIN_STRIKES[2] }, damage: 10, postureDamage: 16 },
  ],
  comboLunge: { ...DEF.comboLunge!, followHits: 7, plantFromHit: 7 },
}
const DEF_TURN_PAIR: AttackDef = { ...DEF_TURN_HITS, comboGapsMs: [...BLADE_SPIN_TURN_PAIR_GAPS_MS] }

let installed = false

/** 剣舞に「回って終わる連」を足す (10-28 夜から配布版・外すのは ?bladeSpin=0)。最初から出る技の組に入れる。 */
export function useBladeDancerSpin(trail: BladeTrailStyle = 'afterimage'): void {
  const turnHits = turnVer > 0 && !oldHits
  const pair = turnHits && !widePair
  ATTACKS[BLADE_SPIN_ID] = pair ? { ...DEF_TURN_PAIR } : turnHits ? { ...DEF_TURN_HITS } : { ...DEF }
  if (!installed) {
    const def = BOSS_DEFINITIONS.find(b => b.id === BOSS_ID)!
    def.baseAttackIds.push(BLADE_SPIN_ID)
    def.unlockByExecutes?.[0]?.ids.push(BLADE_SPIN_ID)
    installed = true
  }
  const tl = turnVer === 0 ? BLADE_SPIN_TIMELINE
    : pair ? (turnVer === 1 ? BLADE_SPIN_TURN_PAIR_TIMELINE : BLADE_SPIN_TURN2_PAIR_TIMELINE)
    : turnHits ? (turnVer === 1 ? BLADE_SPIN_TURN_HITS_TIMELINE : BLADE_SPIN_TURN2_HITS_TIMELINE)
    : turnVer === 1 ? BLADE_SPIN_TURN_TIMELINE : BLADE_SPIN_TURN2_TIMELINE
  BOSS_ATTACK_TIMELINES[BOSS_ID] = { ...(BOSS_ATTACK_TIMELINES[BOSS_ID] ?? {}), [BLADE_SPIN_ID]: tl }
  Object.assign(SMEAR_ARTS, bladeDancerV6Smear(trail, turnHits ? { ...BLADE_SPIN_SMEAR, ...BLADE_SPIN_TURN_SMEAR } : BLADE_SPIN_SMEAR))
  if (pair) {
    for (const prefix of Object.values(TURN_PREFIX)) {
      const k = prefix + TURN_HIT_ART[0]
      const art = SMEAR_ARTS[k] as ContactSmearArc | undefined
      if (art?.afterContact) SMEAR_ARTS[k] = { ...art, afterContact: { ...BLADE_SPIN_PAIR_FIRST_AFTER_CONTACT } }
    }
  }
  preloadBladePetals()
}

/** 外す (テストの後始末用)。 */
export function restoreBladeDancerSpin(): void {
  if (!installed) return
  delete ATTACKS[BLADE_SPIN_ID]
  const def = BOSS_DEFINITIONS.find(b => b.id === BOSS_ID)!
  def.baseAttackIds = def.baseAttackIds.filter(id => id !== BLADE_SPIN_ID)
  const first = def.unlockByExecutes?.[0]
  if (first) first.ids = first.ids.filter(id => id !== BLADE_SPIN_ID)
  delete BOSS_ATTACK_TIMELINES[BOSS_ID]?.[BLADE_SPIN_ID]
  for (const k of [...Object.keys(BLADE_SPIN_SMEAR), ...Object.keys(BLADE_SPIN_TURN_SMEAR)]) delete SMEAR_ARTS[k]
  installed = false
}

/** 配布版で回って終わる連を出すか (10-28 夜から既定で出す・?bladeSpin=0 で外す)。 */
export function parseBladeSpin(v: string | null): boolean {
  return v !== '0'
}
